import { Platform } from "react-native";
import {
  DuiType,
  Flow,
  Purpose,
  UnicoCreateProcessDTO
} from "@repo/core/services/entities/Unico";
import { LastCaptureResponseDTO } from "@repo/core/services/entities/Proposals";
import { RequisitionsResource } from "@repo/core/services/resources/requisitions";
import { ProposalsResource } from "@repo/core/services/resources/proposals";
import { PreProposalData } from "../../../store/preProposalStore";

const parseValidProcessOid = (value: unknown): string | undefined => {
  const trimmed = value == null ? "" : String(value).trim();
  return trimmed !== "" ? trimmed : undefined;
};

async function tryWayUpBioTokenIdReference(
  requisitionsResource: RequisitionsResource,
  clienteDigits: string
): Promise<string | undefined> {
  try {
    const res =
      await requisitionsResource.getBioTokenIdReference(clienteDigits);
    const valida = String(res.data?.valida ?? "")
      .trim()
      .toUpperCase();
    const processoid = parseValidProcessOid(res.data?.processoid);

    return valida === "S" ? processoid : undefined;
  } catch (error) {
    console.error(
      "[UNICO][bioTokenId] WayUp busca-biotokenid-referencia failed",
      {
        clienteDigits,
        error
      }
    );
    return undefined;
  }
}

export const formatCpfForUnico = (cpf: string): string => {
  return cpf?.replace(/\./g, "").replace(/-/g, "") || "";
};

export const createUnicoProcessPayload = (
  proposalData: PreProposalData | null,
  flow: Flow,
  callbackUri: string,
  options?: { bioTokenId?: string }
): UnicoCreateProcessDTO => {
  const payload: UnicoCreateProcessDTO = {
    callbackUri,
    flow,
    purpose: Purpose.creditprocess,
    person: {
      duiType: DuiType.DUI_TYPE_BR_CPF,
      duiValue: formatCpfForUnico(proposalData?.strCliente || ""),
      friendlyName: proposalData?.strNome
    }
  };

  if ((flow === Flow.idtoken || flow === Flow.idcheck) && options?.bioTokenId) {
    payload.bioTokenId = options.bioTokenId;
  }

  return payload;
};

export interface ResolveBioTokenIdOptions {
  proposalsResource?: ProposalsResource;
  lngProposta?: string | number;
  limiteMinutos?: number;
}

/**
 * Resolves bioTokenId for Unico idtoken/idcheck flows.
 * Priority: WayUp reference (valida === "S") → Nest last-result-process-id → Nest last-capture.
 */
export async function resolveBioTokenIdForIdTokenFlow(
  requisitionsResource: RequisitionsResource,
  proposalData: PreProposalData | null,
  options?: ResolveBioTokenIdOptions
): Promise<string | undefined> {
  const clienteDigits = formatCpfForUnico(proposalData?.strCliente || "");
  if (!clienteDigits) {
    console.error("[UNICO][bioTokenId] missing cliente CPF for lookup");
    return undefined;
  }

  const fromWayup = await tryWayUpBioTokenIdReference(
    requisitionsResource,
    clienteDigits
  );
  if (fromWayup) {
    return fromWayup;
  }

  if (!options?.proposalsResource) {
    return undefined;
  }

  try {
    const lastResultResponse =
      await options.proposalsResource.getLastBiometryProcessResult(
        clienteDigits
      );
    const fromLastResult = parseValidProcessOid(
      lastResultResponse.data?.processoid
    );
    if (fromLastResult) {
      return fromLastResult;
    }
  } catch (error) {
    console.error("[UNICO][bioTokenId] Nest last-result-process-id failed", {
      error
    });
  }

  if (options.lngProposta) {
    try {
      const lastCaptureResponse =
        await options.proposalsResource.getLastCapture(
          String(options.lngProposta),
          options.limiteMinutos != null
            ? { limiteMinutos: options.limiteMinutos }
            : undefined
        );
      const fromLastCapture = parseValidProcessOid(
        (
          lastCaptureResponse.data as
            | LastCaptureResponseDTO
            | Record<string, never>
        )?.processoid
      );
      if (fromLastCapture) {
        return fromLastCapture;
      }
    } catch (error) {
      console.error("[UNICO][bioTokenId] Nest last-capture failed", { error });
    }
  }

  return undefined;
}

export const createCallbackUri = (
  verificationId: string,
  baseUrl?: string
): string => {
  if (Platform.OS === "web" && typeof window !== "undefined") {
    return `${window.location.origin}/preProposals/biometryValidation?verificationId=${verificationId}`;
  }
  return baseUrl || "";
};
